"use client";
import React from "react";

interface ImageBlockProps {
  /** Dark variant sits on plum sections; light on paper sections */
  dark?: boolean;
  /** Caption chip text shown on placeholder blocks (names the needed asset) */
  caption: string;
  /** When provided, renders the real asset instead of the striped placeholder */
  src?: string;
  alt: string;
  minHeight?: number;
  radius?: number;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Editorial image slot for the crews redesign. Without `src` it renders the
 * spec's diagonal-stripe placeholder with a caption chip naming the real
 * asset that belongs there — swap in photography before final launch.
 */
const ImageBlock: React.FC<ImageBlockProps> = ({
  dark = false,
  caption,
  src,
  alt,
  minHeight = 360,
  radius = 14,
  className = "",
  style,
}) => {
  const border = dark ? "rgba(255,255,255,0.14)" : "rgba(42,20,53,0.14)";

  if (src) {
    return (
      <div data-component-id="src/components/crews/ImageBlock.tsx:37:6" data-component-path="src/components/crews/ImageBlock.tsx" data-component-line="37" data-component-end-line="52" data-component-file="ImageBlock.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
        className={`relative overflow-hidden ${className}`}
        style={{
          minHeight,
          borderRadius: radius,
          border: `1px solid ${border}`,
          ...style,
        }}
      >
        <img data-component-id="src/components/crews/ImageBlock.tsx:46:8" data-component-path="src/components/crews/ImageBlock.tsx" data-component-line="46" data-component-end-line="51" data-component-file="ImageBlock.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22src%22%3A%22%5Bvar%3Asrc%5D%22%2C%22alt%22%3A%22%5Bvar%3Aalt%5D%22%2C%22className%22%3A%22absolute%20inset-0%20w-full%20h-full%20object-cover%22%7D"
          src={src}
          alt={alt}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <div data-component-id="src/components/crews/ImageBlock.tsx:57:4" data-component-path="src/components/crews/ImageBlock.tsx" data-component-line="57" data-component-end-line="87" data-component-file="ImageBlock.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
      className={`relative overflow-hidden ${className}`}
      role="img"
      aria-label={alt}
      style={{
        minHeight,
        borderRadius: radius,
        border: `1px solid ${border}`,
        background: dark
          ? "repeating-linear-gradient(135deg, rgba(255,255,255,0.05) 0 13px, transparent 13px 26px), #2E1740"
          : "repeating-linear-gradient(135deg, rgba(42,20,53,0.06) 0 13px, transparent 13px 26px), #EFE6DE",
        ...style,
      }}
    >
      <span data-component-id="src/components/crews/ImageBlock.tsx:71:6" data-component-path="src/components/crews/ImageBlock.tsx" data-component-line="71" data-component-end-line="86" data-component-file="ImageBlock.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22absolute%20font-spacemono%22%7D"
        className="absolute font-spacemono"
        style={{
          left: 14,
          bottom: 14,
          fontSize: 12,
          letterSpacing: "0.06em",
          padding: "6px 14px",
          borderRadius: 999,
          background: dark ? "rgba(21,7,32,0.72)" : "rgba(255,255,255,0.78)",
          color: dark ? "#CBB4D5" : "#6E5C7A",
          border: `1px solid ${dark ? "rgba(255,255,255,0.12)" : "rgba(42,20,53,0.12)"}`,
        }}
      >
        {caption}
      </span>
    </div>
  );
};

export default ImageBlock;
